import { Combobox } from '@base-ui/react/combobox';
import { faInfoCircle, faSpinner } from '@fortawesome/pro-light-svg-icons';
import {
  faTimes,
  faChevronDown,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { isArray } from 'lodash';
import debounce from 'lodash/debounce';
import isFunction from 'lodash/isFunction';
import type { ReactElement, ReactNode } from 'react';
import React, {
  useRef,
  useMemo,
  useState,
  useId,
  useTransition,
  useEffect,
} from 'react';
import { Controller } from 'react-hook-form';
import { FormGroup, Label } from 'reactstrap';

import { blue } from '../../../app/colors';
import { useThemeContext } from '../../../app/theme-provider';
import SingleErrorDisplay from '../../error-display/single-error-display';
import { Tooltip } from '../../tooltip';
import styles from '../select-field.module.scss';

export interface SelectFieldOptionProp {
  value: string | number | boolean;
  label: string;
}

export const getOptionValues = (options: SelectFieldOptionProp[]) =>
  options.map((m) => m.value.toString());

interface SelectFieldProps {
  label: string;
  name: string;
  information?: string;
  subLabel?: ReactElement | string;
  placeholder?: string;
  preSearchMessage?: (v?: string) => string;
  noResultsMessage?: (v?: string) => string | ReactNode;
  required?: boolean;
  footer?: ReactElement | string;
  onChange?: (v?: any) => void;
  loadOptions: (v: string) => Promise<any>;
  openOnClick?: boolean;
  openOnFocus?: boolean;
}

interface AsyncMultiComboboxProps {
  id: string;
  label: string;
  placeholder: string;
  required?: boolean;
  theme: string;
  fieldValue: any;
  fieldOnChange: (value: any) => void;
  fieldError: any;
  loadOptions: (v: string) => Promise<any>;
  onChange?: (v?: any) => void;
  noResultsMessage?: (v?: string) => string | ReactNode;
}

function AsyncMultiCombobox({
  id,
  label,
  placeholder,
  required,
  theme,
  fieldValue,
  fieldOnChange,
  fieldError,
  loadOptions,
  onChange,
  noResultsMessage = () => 'No results found',
}: AsyncMultiComboboxProps) {
  const [searchResults, setSearchResults] = useState<SelectFieldOptionProp[]>(
    []
  );
  const [selectedValues, setSelectedValues] = useState<SelectFieldOptionProp[]>(
    []
  );
  const [searchValue, setSearchValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const abortControllerRef = useRef<AbortController | null>(null);
  const debouncedFetchData = useRef<ReturnType<typeof debounce> | null>(null);

  // Initialize debounced function in effect
  useEffect(() => {
    debouncedFetchData.current = debounce(async (searchValue: string) => {
      const controller = new AbortController();
      abortControllerRef.current?.abort();
      abortControllerRef.current = controller;

      setError(null);

      try {
        const result = await loadOptions(searchValue.trim());

        if (controller.signal.aborted) {
          return;
        }

        startTransition(() => {
          setSearchResults(result || []);
        });
      } catch {
        if (controller.signal.aborted) {
          return;
        }
        startTransition(() => {
          setError('Failed to load options. Please try again.');
        });
      }
    }, 300);

    return () => {
      debouncedFetchData.current?.cancel();
    };
  }, [loadOptions]);

  // Sync selectedValues with fieldValue changes
  useEffect(() => {
    if (isArray(fieldValue)) {
      setSelectedValues(
        fieldValue.map((v: string) => ({ value: v, label: v }))
      );
    } else {
      setSelectedValues([]);
    }
  }, [fieldValue]);

  const trimmedSearchValue = searchValue.trim();

  const items = useMemo(() => {
    const allItems = [...searchResults];
    const selectedArray = isArray(selectedValues) ? selectedValues : [];

    for (const selectedValue of selectedArray) {
      if (!searchResults.some((opt) => opt.value === selectedValue.value)) {
        allItems.push(selectedValue);
      }
    }

    return allItems;
  }, [searchResults, selectedValues]);

  function getStatus() {
    if (isPending) {
      return (
        <>
          <FontAwesomeIcon icon={faSpinner} spin aria-hidden />
          {' Searching…'}
        </>
      );
    }

    if (error) {
      return error;
    }

    if (trimmedSearchValue === '') {
      const selectedArray = isArray(selectedValues) ? selectedValues : [];
      return selectedArray.length > 0 ? null : 'Start typing to search…';
    }

    if (searchResults.length === 0) {
      return `No matches for "${trimmedSearchValue}".`;
    }

    return null;
  }

  function getEmptyMessage() {
    if (
      trimmedSearchValue === '' ||
      isPending ||
      searchResults.length > 0 ||
      error
    ) {
      return null;
    }

    return typeof noResultsMessage === 'function'
      ? noResultsMessage(trimmedSearchValue)
      : noResultsMessage;
  }

  const status = getStatus();
  const emptyMessage = getEmptyMessage();

  return (
    <div className="mb-2">
      <Combobox.Root
        multiple
        value={selectedValues}
        items={items}
        itemToStringLabel={(item: SelectFieldOptionProp) => item.label}
        filter={null}
        onOpenChange={(open) => {
          const selectedArray = Array.isArray(selectedValues)
            ? selectedValues
            : [];
          if (!open && selectedArray.length > 0) {
            setSearchResults(selectedArray);
          }
        }}
        onValueChange={(nextSelectedValues) => {
          const valuesArray = isArray(nextSelectedValues)
            ? nextSelectedValues
            : [];
          setSelectedValues(valuesArray);
          const valueArray = valuesArray.map((v) => v.value);
          fieldOnChange(valueArray);
          setSearchValue('');
          setError(null);
          if (isFunction(onChange)) {
            onChange(valuesArray);
          }
        }}
        onInputValueChange={(nextSearchValue, eventDetails) => {
          setSearchValue(nextSearchValue);

          if (nextSearchValue === '') {
            debouncedFetchData.current?.cancel();
            abortControllerRef.current?.abort();
            setSearchResults([]);
            setError(null);
            return;
          }

          if (eventDetails.reason === 'item-press') {
            return;
          }

          debouncedFetchData.current?.(nextSearchValue);
        }}
      >
        <Combobox.InputGroup
          className={cx(styles.inputGroup, styles.multiInputGroup, {
            [styles.error]: fieldError,
            [styles.dark]: theme === 'dark',
          })}
        >
          <div className={styles.multiValueContainer}>
            <Combobox.Value>
              {(
                selectedItems:
                  | SelectFieldOptionProp[]
                  | SelectFieldOptionProp
                  | null
              ) => {
                const items = isArray(selectedItems)
                  ? selectedItems
                  : selectedItems
                    ? [selectedItems]
                    : [];
                return (
                  <Combobox.Chips>
                    {items.map((item) => (
                      <Combobox.Chip
                        key={item.value.toString()}
                        className={cx(styles.multiValue, {
                          [styles.dark]: theme === 'dark',
                        })}
                      >
                        <span className={styles.multiValueLabel}>
                          {item.label}
                        </span>
                        <Combobox.ChipRemove
                          className={styles.multiValueRemove}
                          aria-label={`Remove ${item.label}`}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Combobox.ChipRemove>
                      </Combobox.Chip>
                    ))}
                  </Combobox.Chips>
                );
              }}
            </Combobox.Value>
            <Combobox.Input
              id={id}
              placeholder={selectedValues.length > 0 ? '' : placeholder}
              className={styles.multiInput}
              aria-label={label}
              required={required}
            />
          </div>
          <div className={styles.actionButtons}>
            <Combobox.Clear
              className={styles.clear}
              aria-label="Clear all selections"
              tabIndex={0}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Combobox.Clear>
            <Combobox.Trigger
              className={styles.trigger}
              aria-label="Open popup"
              tabIndex={0}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </Combobox.Trigger>
          </div>
        </Combobox.InputGroup>

        <Combobox.Portal>
          <Combobox.Positioner className={styles.positioner} sideOffset={4}>
            <Combobox.Popup
              className={cx(styles.popup, {
                [styles.dark]: theme === 'dark',
              })}
              aria-busy={isPending || undefined}
            >
              <div className={styles.viewport}>
                <Combobox.Status>
                  {status ? (
                    <div className={styles.status} aria-live="polite">
                      {status}
                    </div>
                  ) : null}
                </Combobox.Status>
                <Combobox.Empty>
                  {emptyMessage ? (
                    <div className={styles.empty}>{emptyMessage}</div>
                  ) : null}
                </Combobox.Empty>
                <Combobox.List>
                  {(item: SelectFieldOptionProp) => (
                    <Combobox.Item
                      key={item.value.toString()}
                      className={styles.item}
                      value={item}
                    >
                      <Combobox.ItemIndicator className={styles.itemIndicator}>
                        <FontAwesomeIcon icon={faCheck} />
                      </Combobox.ItemIndicator>
                      <span className={styles.itemText}>{item.label}</span>
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </div>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
    </div>
  );
}

export default function AsyncMultiSelectField({
  label,
  name,
  required,
  onChange,
  loadOptions,
  information,
  subLabel,
  placeholder = '',
  noResultsMessage = () => 'No results found',
  footer,
}: SelectFieldProps) {
  const { theme } = useThemeContext();
  const id = useId();

  return (
    <FormGroup>
      {label && (
        <>
          <Label
            for={id}
            name={label || name}
            data-testid={name}
            className={cx('me-2', { required })}
          >
            {label || name}
          </Label>
          {information && (
            <Tooltip tooltip={information}>
              <FontAwesomeIcon
                icon={faInfoCircle}
                size="lg"
                color={theme === 'dark' ? '#52baff' : blue}
                tabIndex={0}
              />
            </Tooltip>
          )}
        </>
      )}
      {subLabel &&
        (typeof subLabel === 'string' ? (
          <p className="text-muted mb-3">{subLabel}</p>
        ) : (
          subLabel
        ))}

      <Controller
        name={name}
        render={({ field, fieldState }) => (
          <AsyncMultiCombobox
            id={id}
            label={label}
            placeholder={placeholder}
            required={required}
            theme={theme}
            fieldValue={field.value}
            fieldOnChange={field.onChange}
            fieldError={fieldState.error}
            loadOptions={loadOptions}
            onChange={onChange}
            noResultsMessage={noResultsMessage}
          />
        )}
      />
      <SingleErrorDisplay field={name} />
      {footer && <div>{footer}</div>}
    </FormGroup>
  );
}
